import type { Attachment } from '@sentry/core';
import usePersistentStore from '@gui/store/persistent/persistentStore';

type CachedCanvasFrame = {
    dataUrl: string;
    capturedAt: number;
};

let cachedCanvasFrame: CachedCanvasFrame | null = null;

export function getPersistedStateAttachment(): Attachment | null {
    const storageName = getPersistedStateStorageName();
    if (!storageName) return null;

    const raw = localStorage.getItem(storageName);
    if (!raw) return null;

    let payload = raw;
    try {
        payload = JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        // keep raw if parsing fails
    }

    return {
        data: payload,
        filename: `crash_state_${storageName}_${Date.now()}.json`,
        contentType: 'application/json',
    };
}

export function getPersistedStateStorageName(): string | undefined {
    return usePersistentStore.persist.getOptions().name;
}

export function cacheCanvasFrame(dataUrl: string) {
    cachedCanvasFrame = {
        dataUrl,
        capturedAt: Date.now(),
    };
}

export function getCachedCanvasFrameAttachment(): Attachment | null {
    if (!cachedCanvasFrame?.dataUrl) return null;

    const bytes = dataUrlToUint8Array(cachedCanvasFrame.dataUrl);
    if (bytes.length === 0) return null;

    return {
        data: bytes,
        filename: `crash_screenshot_${cachedCanvasFrame.capturedAt}.png`,
        contentType: 'image/png',
    };
}

export function getCachedCanvasFrameMetadata(): { capturedAt: number } | null {
    if (!cachedCanvasFrame) return null;

    return { capturedAt: cachedCanvasFrame.capturedAt };
}

function dataUrlToUint8Array(dataUrl: string): Uint8Array {
    const base64 = dataUrl.split(',')[1];
    if (!base64) return new Uint8Array();

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}
